import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import TenantBrandChip from '~/components/Common/Elements/TenantBrandChip';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { useGetAllCustomerServiceRepAssignments } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';
import { useGetCustomerServiceReps } from '~/hooks/apis/loyalty/useCustomerServiceReps';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';

import type { CustomerServiceRep } from '~/services/LuxLoyaltyCustomerServiceRepService';
import { formatDateSlashes } from '~/services/TimeService';
import UsersService from '~/services/UsersService';

import { getUserTenant, getUserTenantTitle } from '~/utils/userIdentity';

import AssignmentReassignDialog from './AssignmentReassignDialog';
import AssignmentsToolbar from './AssignmentsToolbar';
import { filterAssignmentMemberSearch } from './assignmentMemberSearch';

interface CustomerOption {
  id: string;
  fullName: string;
  email: string;
  tenant?: string;
}

const MIN_SEARCH_LENGTH = 3;

const DEFAULT_ASSIGNMENTS_PAGE_SIZE = 25;
const ASSIGNMENTS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

interface ReassignTarget {
  customerId: string;
  memberLabel: string;
  currentRepId: string | null;
}

interface CustomerSummary {
  fullName: string | null;
  email: string | null;
  tenant?: string;
}

type AssignmentGridRow = {
  id: string;
  customerId: string;
  accountNumber: number;
  memberName: string;
  memberEmail: string;
  representativeName: string;
  representativeDetail: string;
  updatedAt: string;
  currentRepId: string | null;
};

function initialsFromName(name: string | null | undefined): string {
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

const GRID_SX = {
  border: 0,
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'text.secondary',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-cell': {
    alignItems: 'center',
    display: 'flex',
    lineHeight: 'normal',
  },
} as const;

/** Assignments tab: list members with their assigned rep + per-row reassign. */
export default function AssignmentsTab() {
  const [repFilter, setRepFilter] = useState<string>('all');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_ASSIGNMENTS_PAGE_SIZE,
  });
  const [customerInput, setCustomerInput] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const repsQuery = useGetCustomerServiceReps({ page: 1, pageSize: 50 });
  const repId = repFilter === 'all' ? undefined : repFilter;
  const assignmentsQuery = useGetAllCustomerServiceRepAssignments({
    customerServiceRepId: repId,
    customerId: selectedCustomer?.id,
  });
  // Search stays on the full assignment list. The grid query narrows once a member is picked.
  const searchableAssignmentsQuery = useGetAllCustomerServiceRepAssignments({
    customerServiceRepId: repId,
  });
  const queryClient = useQueryClient();
  const [visibleRowIds, setVisibleRowIds] = useState<Array<GridRowId>>([]);

  const handleVisibleRowIds = useCallback((ids: Array<GridRowId>) => {
    setVisibleRowIds((current) => {
      if (current.length === ids.length && current.every((id, index) => id === ids[index])) {
        return current;
      }
      return ids;
    });
  }, []);

  const reps = repsQuery.data?.result.reps ?? [];
  const assignments = useMemo(() => assignmentsQuery.data?.accounts ?? [], [assignmentsQuery.data?.accounts]);
  const totalRecords = assignmentsQuery.data?.totalRecords ?? assignments.length;
  const assignmentCustomerIds = useMemo(
    () => [...new Set((searchableAssignmentsQuery.data?.accounts ?? []).map((account) => account.customerId))],
    [searchableAssignmentsQuery.data?.accounts],
  );
  const memberSearch = customerInput.trim();
  const memberSummariesQuery = useGetUsersSummaryByIds({
    userIds: assignmentCustomerIds,
    brand: LUXURY_ESCAPES,
    enabled: memberSearch.length >= MIN_SEARCH_LENGTH,
  });
  const customerOptions = useMemo(() => {
    if (memberSearch.length < MIN_SEARCH_LENGTH || !memberSummariesQuery.data) {
      return [];
    }

    const members: Array<CustomerOption> = assignmentCustomerIds.map((customerId) => {
      const user = memberSummariesQuery.data.get(customerId);
      return {
        id: customerId,
        fullName: user?.full_name || user?.email || customerId,
        email: user?.email ?? '',
        tenant: user?.tenant,
      };
    });

    return filterAssignmentMemberSearch(members, memberSearch);
  }, [assignmentCustomerIds, memberSearch, memberSummariesQuery.data]);
  const customerLoading =
    memberSearch.length >= MIN_SEARCH_LENGTH &&
    (searchableAssignmentsQuery.isLoading || memberSummariesQuery.isLoading || memberSummariesQuery.isFetching);

  useEffect(() => {
    if (!memberSummariesQuery.isError) {
      return;
    }
    enqueueSnackbar('Failed to search Infinite members.', { variant: 'error' });
  }, [enqueueSnackbar, memberSummariesQuery.isError]);

  const visibleCustomerIds = useMemo(() => {
    const customerIdByRowId = new Map(assignments.map((row) => [row.id, row.customerId]));
    const ids: Array<string> = [];
    const seen = new Set<string>();
    visibleRowIds.forEach((rowId) => {
      const customerId = customerIdByRowId.get(String(rowId));
      if (customerId && !seen.has(customerId)) {
        seen.add(customerId);
        ids.push(customerId);
      }
    });
    return ids;
  }, [assignments, visibleRowIds]);

  // Only fetch user records for the DataGrid's current page. Shared 'users' query
  // key so other pages can hit the cache; previously visited names stay in cache.
  const customerQueries = useQueries({
    queries: visibleCustomerIds.map((customerId) => ({
      queryKey: ['users', customerId] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        UsersService.getUser(customerId, { signal }) as Promise<App.User>,
      staleTime: 60_000,
    })),
  });

  const customerDataUpdatedAt = customerQueries.map((query) => query.dataUpdatedAt).join(',');
  const customersById = useMemo(() => {
    void customerDataUpdatedAt;
    const map: Record<string, CustomerSummary> = {};
    assignments.forEach((row) => {
      const user = queryClient.getQueryData<App.User>(['users', row.customerId]);
      if (!user) {
        return;
      }
      map[row.customerId] = {
        fullName: user.fullName ?? null,
        email: user.email ?? null,
        tenant: getUserTenant(user),
      };
    });
    return map;
  }, [assignments, customerDataUpdatedAt, queryClient]);

  const [reassignTarget, setReassignTarget] = useState<ReassignTarget | null>(null);

  const rows = useMemo<Array<AssignmentGridRow>>(
    () =>
      assignments.map((row) => {
        const customer = customersById[row.customerId];
        const rep: CustomerServiceRep | null = row.customerServiceRep;
        return {
          id: row.id,
          customerId: row.customerId,
          accountNumber: row.accountNumber,
          memberName: customer?.fullName ?? '',
          memberEmail: customer?.email ?? '',
          representativeName: rep ? `${rep.firstName} ${rep.lastName}`.trim() : 'Unassigned',
          representativeDetail: rep ? `${rep.email} · ${rep.phone}` : '',
          updatedAt: row.updatedAt,
          currentRepId: rep?.id ?? null,
        };
      }),
    [assignments, customersById],
  );

  const columns = useMemo<Array<GridColDef<AssignmentGridRow>>>(
    () => [
      {
        field: 'memberName',
        headerName: 'Member',
        minWidth: 260,
        flex: 1.2,
        valueGetter: (_value, row) => [row.memberName, row.memberEmail].filter(Boolean).join(' '),
        renderCell: ({ row }: GridRenderCellParams<AssignmentGridRow>) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={(theme) => ({
                width: 32,
                height: 32,
                fontSize: 13,
                fontWeight: 700,
                background: row.memberName
                  ? theme.palette.loyaltyTiers.infinite.gradient
                  : theme.palette.action.disabledBackground,
                color: row.memberName ? 'common.white' : 'text.disabled',
              })}
            >
              {initialsFromName(row.memberName)}
            </Avatar>
            <Box sx={{ minWidth: 0, lineHeight: 1.25 }}>
              {row.memberName ? (
                <MuiLink
                  component={Link}
                  to={`/users/${row.customerId}`}
                  variant="body2"
                  fontWeight={600}
                  underline="hover"
                  color="text.primary"
                  display="block"
                >
                  {row.memberName}
                </MuiLink>
              ) : (
                <Typography variant="body2" fontWeight={600} component="em" sx={{ opacity: 0.7 }}>
                  Loading…
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.25 }}>
                {row.memberEmail || '—'}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: 'customerId',
        headerName: 'Customer ID',
        minWidth: 280,
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<AssignmentGridRow>) => (
          <MuiLink
            component={Link}
            to={`/users/${row.customerId}`}
            variant="caption"
            underline="hover"
            sx={{ fontFamily: 'monospace' }}
          >
            {row.customerId}
          </MuiLink>
        ),
      },
      {
        field: 'representativeName',
        headerName: 'Assigned representative',
        minWidth: 260,
        flex: 1.1,
        valueGetter: (_value, row) => [row.representativeName, row.representativeDetail].filter(Boolean).join(' '),
        renderCell: ({ row }: GridRenderCellParams<AssignmentGridRow>) =>
          row.currentRepId ? (
            <Stack sx={{ lineHeight: 1.25 }}>
              <Typography variant="body2" fontWeight={600}>
                {row.representativeName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.representativeDetail}
              </Typography>
            </Stack>
          ) : (
            <Chip size="small" color="warning" label="Unassigned" />
          ),
      },
      {
        field: 'updatedAt',
        headerName: 'Last updated',
        minWidth: 140,
        flex: 0.5,
        type: 'dateTime',
        valueGetter: (_value, row) => (row.updatedAt ? new Date(row.updatedAt) : null),
        valueFormatter: (value) => (value ? formatDateSlashes(value) : ''),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 110,
        flex: 0.4,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }: GridRenderCellParams<AssignmentGridRow>) => (
          <MuiLink
            component="button"
            type="button"
            variant="body2"
            onClick={() =>
              setReassignTarget({
                customerId: row.customerId,
                memberLabel: row.memberName || `Member #${row.accountNumber}`,
                currentRepId: row.currentRepId,
              })
            }
            disabled={reps.length === 0}
          >
            Reassign
          </MuiLink>
        ),
      },
    ],
    [reps.length],
  );

  return (
    <Box>
      <Stack mb={3} spacing={1}>
        <Typography variant="h6">Assignments</Typography>
        <Typography variant="body2" color="text.secondary">
          Assign a dedicated concierge representative to every Infinite member. Assignments drive the concierge details
          on each member&apos;s Infinite landing page.
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Changes publish to each member&apos;s Infinite landing page automatically — concierge <strong>name</strong>,{' '}
        <strong>mobile</strong> and <strong>email</strong> update on save.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
        <Autocomplete
          sx={{ flex: 1 }}
          size="small"
          options={customerOptions}
          value={selectedCustomer}
          inputValue={customerInput}
          loading={customerLoading}
          getOptionLabel={(opt) => {
            const tenantTitle = getUserTenantTitle(opt);
            return opt.email ? `${opt.fullName} (${opt.email})${tenantTitle ? ` — ${tenantTitle}` : ''}` : opt.fullName;
          }}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(x) => x}
          onInputChange={(_, value) => {
            setCustomerInput(value);
          }}
          onChange={(_, value) => {
            setSelectedCustomer(value);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search Infinite member by name, email or id"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={opt.id}>
              <Stack>
                <Typography variant="body2" fontWeight={600}>
                  {opt.fullName || opt.email || opt.id}
                </Typography>
                {opt.email && (
                  <Typography variant="caption" color="text.secondary">
                    {opt.email}
                  </Typography>
                )}
                {opt.tenant && <TenantBrandChip tenantValue={opt.tenant} />}
              </Stack>
            </li>
          )}
          noOptionsText={
            customerInput.length > 0 && customerInput.length < MIN_SEARCH_LENGTH
              ? `Type at least ${MIN_SEARCH_LENGTH} characters`
              : 'No Infinite members found'
          }
        />
        <TextField
          select
          size="small"
          label="Rep"
          value={repFilter}
          onChange={(e) => {
            setRepFilter(e.target.value);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">All representatives</MenuItem>
          {reps.map((rep) => (
            <MenuItem key={rep.id} value={rep.id}>
              {rep.firstName} {rep.lastName}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" mb={1}>
        <Typography variant="caption" color="text.secondary">
          {totalRecords} member{totalRecords === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {assignmentsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load assignments.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={assignmentsQuery.isLoading}
          pagination
          paginationMode="client"
          sortingMode="client"
          filterMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[...ASSIGNMENTS_PAGE_SIZE_OPTIONS]}
          slots={{ toolbar: AssignmentsToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 300 },
              onVisibleRowIds: handleVisibleRowIds,
            },
          }}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={64}
          localeText={{
            noRowsLabel: 'No assignments match the current filters.',
          }}
          sx={GRID_SX}
        />
      </Paper>

      <AssignmentReassignDialog
        open={!!reassignTarget}
        onClose={() => setReassignTarget(null)}
        customerId={reassignTarget?.customerId ?? ''}
        memberLabel={reassignTarget?.memberLabel ?? ''}
        currentRepId={reassignTarget?.currentRepId ?? null}
        reps={reps}
      />
    </Box>
  );
}
